'use client';

import { css } from '@onbeam/business-styled-system/css';
import {
  center,
  flex,
  grid,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import { AnimatePresence, HTMLMotionProps, motion } from 'motion/react';
import { navItems, socials } from '../Header.utils';
import { MobileNavItem } from './MobileNavItem';
import NextLink from 'next/link';
import { useMobileNavigation } from './MobileNavigation.store';
import { BuyBeamSubMenu } from './BuyBeamSubMenu';
import { ForDevelopersSubMenu } from './ForDevelopersSubMenu';
import { useEffect } from 'react';
import { useBreakpoint, useIsMounted } from '@onbeam/utils';
import { FocusTrap } from '@/components/shared/FocusTrap';
import { RemoveScroll } from 'react-remove-scroll';
import { BeamLogo } from '@/icons/custom';
import { createPortal } from 'react-dom';
import { CloseIcon, MenuIcon } from '@onbeam/icons/business';

const getStaggeredTransition = (index: number): HTMLMotionProps<'div'> => ({
  initial: { opacity: 0, y: -10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      delay: 0.2 + index * 0.03,
      ease: EASING,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      delay: 0,
      duration: 0.2,
      ease: EASING,
    },
  },
});

const EASING = 'easeOut';

export const MobileNavigation = () => {
  const isOpen = useMobileNavigation((state) => state.isOpen);
  const isMobile = useBreakpoint({ max: '60rem' });
  const isMounted = useIsMounted();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    useMobileNavigation.setState({ isOpen: false });
  }, [isMobile]);

  const setOpen = (open: boolean) =>
    useMobileNavigation.setState({ isOpen: open });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={center({
          rounded: 'full',
          transition: 'opacity',
          cursor: 'pointer',
          _hoverFocus: {
            opacity: 0.8,
          },
          '@media(min-width: calc(60rem + 1px))': { display: 'none' },
        })}
      >
        <MenuIcon />
      </button>
      {isMounted &&
        createPortal(
          <AnimatePresence initial={false}>
            {isOpen && (
              <FocusTrap>
                <RemoveScroll allowPinchZoom>
                  <motion.div
                    initial={{ y: '-100dvh' }}
                    animate={{
                      y: 0,
                    }}
                    exit={{
                      y: '-100dvh',
                    }}
                    transition={{
                      duration: 0.4,
                      ease: EASING,
                    }}
                    className={vstack({
                      position: 'fixed',
                      inset: '0',
                      bg: 'mono.primaryFg',
                      zIndex: 100,
                    })}
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        transition: {
                          delay: 0.3,
                          ease: EASING,
                        },
                      }}
                      exit={{
                        opacity: 0,
                        transition: { delay: 0, ease: EASING },
                      }}
                      className={flex({
                        justify: 'space-between',
                        align: 'center',
                        w: 'full',
                        px: '6',
                        py: '5',
                      })}
                    >
                      <NextLink
                        href="/"
                        className={css({ _focus: { outline: 'none' } })}
                        onClick={() => setOpen(false)}
                      >
                        <BeamLogo />
                        <span className={css({ srOnly: true })}>Home</span>
                      </NextLink>
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className={center({
                          rounded: 'full',
                          transition: 'opacity',
                          cursor: 'pointer',
                          zIndex: 999,
                          _hoverFocus: {
                            opacity: 0.8,
                          },
                        })}
                      >
                        <CloseIcon />
                      </button>
                    </motion.div>
                    <div
                      className={vstack({
                        p: '6',
                        overflowY: 'auto',
                        flexGrow: 1,
                        gap: '[6.25rem]',
                      })}
                    >
                      <div className={vstack({ gap: '10' })}>
                        <div
                          data-open={isOpen}
                          className={vstack({
                            gap: '2.5',
                          })}
                        >
                          {navItems.map((item, index) => (
                            <motion.div
                              key={item.label}
                              {...getStaggeredTransition(index)}
                            >
                              <MobileNavItem {...item} />
                            </motion.div>
                          ))}
                        </div>
                        <motion.div
                          {...getStaggeredTransition(navItems.length)}
                          className={vstack({
                            gap: '2.5',
                            md: {
                              flexDirection: 'row',
                            },
                          })}
                        >
                          <BuyBeamSubMenu />
                          <ForDevelopersSubMenu />
                        </motion.div>
                      </div>
                      <motion.div
                        {...getStaggeredTransition(navItems.length + 1)}
                        className={grid({
                          columns: 2,
                          mt: 'auto',
                          gap: '6',
                          '@media(min-width: 23.75rem)': {
                            gridTemplateColumns: '3',
                          },
                          md: {
                            gridTemplateColumns: '6',
                          },
                        })}
                      >
                        {socials.map(({ icon: Icon, ...social }) => (
                          <NextLink
                            key={social.label}
                            href={social.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={flex({
                              py: '2.5',
                              gap: '2',
                              transition: 'colors',
                              _hoverFocus: {
                                color: 'mono.tertiary',
                              },
                            })}
                          >
                            <Icon height={18} width={18} />
                            <span className={text({ style: 'body-sm' })}>
                              {social.label}
                            </span>
                          </NextLink>
                        ))}
                      </motion.div>
                    </div>
                  </motion.div>
                </RemoveScroll>
              </FocusTrap>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
};
